import React, { FC, useContext, useEffect, useState } from 'react';
import { ContextApi } from 'react-uforms';
import { Button } from '@ch/common/components/button';
import { Question, ValidationTypes } from '@ch/common/types/data';
import { QuizContext } from './quiz-context';
import { useData } from '../use-data';
import { IconDelete } from '@ch/common/components/icon';
import { QuizFieldDuplicatorStyled } from './quiz.styled';
import QuizField from './quiz-field';

const QuizFieldDuplicator: FC<Question & { subQuestions?: Question[] }> = ({
  identifier: name,
  question: buttonTitle,
  subQuestions,
  maxSections = Infinity,
}) => {
  const api = useContext(ContextApi);
  const {
    data: { questions: rawQuestions },
    insertQuestions,
    removeQuestionsByGroup,
  } = useData();
  const { resetUserQuestions, userQuestions } = useContext(QuizContext);
  const [groupIndex, setGroupIndex] = useState(1); // zero for root group
  const [appliedIndexes, setAppliedIndexes] = useState<number[]>([]);
  const [duplicatorQuestions, setQuestions] = useState<Question[]>([]);
  useEffect(() => {
    api && resetUserQuestions(api);
  }, [groupIndex, api, resetUserQuestions]);

  useEffect(() => {
    const filtered = subQuestions?.filter(q => q.duplicatorGroup?.startsWith(name)) ?? [];
    setQuestions(filtered);
  }, [subQuestions, name]);

  const onAddQuestions = () => {
    const questionIdentifiers = api.getValue(name) as string[];
    const duplicatorPosition = userQuestions.findIndex(q => q.identifier === name);

    const questions = rawQuestions
      .filter(question => questionIdentifiers.includes(question.identifier))
      .map(question => {
        let adaptIdx = groupIndex;
        //preventing the same field on the form (nested duplicators issue)
        while (api.getValue(`${question.identifier}_${adaptIdx}`) !== undefined) {
          adaptIdx++;
        }
        return {
          ...question,
          duplicatorGroup: `${name}_${groupIndex}`,
          identifier: `${question.identifier}_${adaptIdx}`,
          validations: question.validations.map(el =>
            [ValidationTypes.MAXDATE, ValidationTypes.MINDATE].includes(el.type) &&
            questionIdentifiers.includes(el.value as string) // if group has questions that have been repeated we need to update validation rules to match these questions
              ? {
                  ...el,
                  value: `${el.value}_${groupIndex}`,
                }
              : el,
          ),
          ...(question.conditions && {
            conditions: question.conditions.map(condition => ({
              ...condition,
              parent: condition.parent.map(parent => ({
                ...parent,
                identifier: questionIdentifiers.includes(parent.identifier)
                  ? `${parent.identifier}_${groupIndex}`
                  : parent.identifier,
              })),
            })),
          }),
        };
      });
    insertQuestions(questions, duplicatorPosition);
    setAppliedIndexes([...appliedIndexes, groupIndex]);
    setGroupIndex(groupIndex + 1);
  };

  const onRemoveQuestions = (groupName: string, groupIdx: number) => {
    setAppliedIndexes(appliedIndexes.filter(idx => idx !== groupIdx));
    removeQuestionsByGroup(groupName);
    api && resetUserQuestions(api);
  };

  return (
    <>
      {duplicatorQuestions.length > 0 &&
        appliedIndexes.map(groupIdx => {
          const groupName = `${name}_${groupIdx}`;
          return (
            <QuizFieldDuplicatorStyled $bgColor="grayLighter" $m={[15, 0, 0]} $p={[15]} key={groupName}>
              <IconDelete $size={16} $color="grayDarker" onClick={() => onRemoveQuestions(groupName, groupIdx)} />
              {duplicatorQuestions
                .filter(question => question.duplicatorGroup.match(`${groupName}$`))
                .map(question => (
                  <QuizField key={question.identifier} {...{ ...question, subQuestions }} />
                ))}
            </QuizFieldDuplicatorStyled>
          );
        })}

      {appliedIndexes.length < maxSections && (
        <Button
          $variant="link"
          $color="tertiaryBlue"
          $hoverTextDecoration="underline"
          $size="sm"
          onClick={onAddQuestions}
        >
          {buttonTitle}
        </Button>
      )}
    </>
  );
};

export default QuizFieldDuplicator;
